import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import Reveal from "@/components/Reveal";
import SectionKicker from "@/components/SectionKicker";
import MagneticButton from "@/components/MagneticButton";
import FAQSection from "@/components/FAQSection";
import ScrollConnectedPath, { PathNode } from "@/components/ScrollConnectedPath";
import { useCtaPanel } from "@/components/StartProjectPanel";
import { features, builtForIndia, compliance, status } from "@/data/schoolos";
import { schoolosFaq } from "@/data/schoolosFaq";

const EASE = [0.22, 1, 0.36, 1];

// Panel context for SchoolOS leads — tags the Mongo document via `source`
const SCHOOLOS_CTA = {
  source: "schoolos-early-access",
  eyebrow: "Early access",
  title: "Shape it with us.",
  subtitle:
    "Three fields, fifteen seconds. We're working with a small number of schools during development — tell us about yours.",
  messageLabel: "Your school / group",
  messagePlaceholder: "Who runs administration on your side?",
};

function FeatureBlock({ feature, i }) {
  const ref = useRef(null);
  const active = useInView(ref, { amount: 0.3 });

  return (
    <div
      ref={ref}
      className="relative border-l border-white/10 pl-5 md:border-l-0 md:pl-0"
      data-testid={`schoolos-feature-${i}`}
    >
      <PathNode index={i + 1} active={active} top={18} />
      <Reveal>
        <h3 className="font-heading font-bold tracking-normal text-2xl md:text-3xl text-white">
          {feature.title}
        </h3>
        <p className="mt-3 max-w-2xl text-zinc-400 leading-relaxed">{feature.desc}</p>
      </Reveal>
    </div>
  );
}

export default function Schoolos() {
  const ctaPanel = useCtaPanel();

  return (
    <div data-testid="schoolos-page">
      {/* HERO */}
      <section className="relative pt-32 pb-14 md:pt-52 md:pb-24 overflow-hidden">
        <div className="tech-grid grid-fade absolute inset-0 opacity-40" />
        <div className="glow-orb animate-pulse-glow absolute -top-10 right-[12%] h-[420px] w-[420px]" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <SectionKicker index="[07]">Product</SectionKicker>
          </Reveal>
          {/* Stacked mask-reveal lines — same pattern as the Payroll hero */}
          <h1 className="mt-6 font-heading font-black uppercase tracking-normal text-5xl md:text-7xl lg:text-8xl text-white leading-[0.9] max-w-5xl">
            <span className="block overflow-hidden">
              <motion.span
                className="inline-block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.1, duration: 0.8, ease: EASE }}
              >
                Kedbyte
              </motion.span>
            </span>
            <span className="block overflow-hidden">
              <motion.span
                className="inline-block text-cyan-accent text-glow"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.22, duration: 0.8, ease: EASE }}
              >
                SchoolOS.
              </motion.span>
            </span>
            <span className="sr-only">
              {" "}— school management system for Indian K-12 schools (in active development)
            </span>
          </h1>

          {/* Status badge — claims policy: in-development state visible near the top */}
          <Reveal delay={0.18}>
            <div
              className="mt-7 inline-flex items-center gap-2.5 rounded-full border border-cyan-accent/40 bg-cyan-accent/5 px-4 py-2"
              data-testid="schoolos-status-badge"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-cyan-accent animate-pulse-glow" />
              <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-cyan-accent">
                In active development
              </span>
            </div>
          </Reveal>

          <Reveal delay={0.25}>
            <p className="mt-8 max-w-2xl text-zinc-400 text-lg leading-relaxed">
              A school management system for Indian K-12 schools, built to be
              correct about children's data before it is convenient. Built by
              Kedbyte Technologies, the studio behind our{" "}
              <Link
                to="/services"
                className="text-zinc-300 underline decoration-white/20 underline-offset-4 hover:text-cyan-accent transition-colors"
              >
                custom software development services
              </Link>
              .
            </p>
          </Reveal>

          <Reveal delay={0.32}>
            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-5">
              <MagneticButton
                onClick={() => ctaPanel.open(SCHOOLOS_CTA)}
                testid="schoolos-hero-cta"
              >
                Get in touch
              </MagneticButton>
              <MagneticButton to="/contact" variant="ghost" testid="schoolos-talk-cta">
                Talk to us
              </MagneticButton>
            </div>
          </Reveal>

          {/* Page-intro hairline draws beneath the header */}
          <motion.div
            className="mt-14 h-px w-full origin-left bg-white/10"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.5 }}
          />
        </div>
      </section>

      {/* WHAT IT IS / WHO IT'S FOR */}
      <section className="pb-14 md:pb-28" data-testid="schoolos-audience">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-10 md:gap-16">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-cyan-accent">
              What it is
            </p>
            <h2 className="mt-4 font-heading font-bold tracking-normal text-2xl md:text-3xl text-white">
              One system for the school
            </h2>
            <p className="mt-4 text-zinc-400 leading-relaxed">
              SchoolOS is being built as a school ERP for Indian K-12 schools —
              student records, staff, attendance, fees, examinations, timetables
              and documents in one system, with separate access for
              administrators, teachers and parents.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-400">
              Who it's for
            </p>
            <h2 className="mt-4 font-heading font-bold tracking-normal text-2xl md:text-3xl text-white">
              Schools & multi-campus groups
            </h2>
            <p className="mt-4 text-zinc-400 leading-relaxed">
              Single schools and groups running multiple campuses in one system,
              with staff access scoped to their posting — and the crest and
              colours of each school, not ours.
            </p>
          </Reveal>
        </div>
      </section>

      {/* WHAT MAKES IT DIFFERENT — rides the studio's pipeline rail */}
      <section className="relative py-14 md:py-28 border-t border-white/10" data-testid="schoolos-features">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <SectionKicker index="[01]">What makes it different</SectionKicker>
          </Reveal>
          <Reveal delay={0.1} variant="mask">
            <h2 className="mt-4 mb-14 font-heading font-bold tracking-normal text-4xl md:text-5xl text-white max-w-3xl">
              Correct about children's data. By construction.
            </h2>
          </Reveal>

          <ScrollConnectedPath>
            <div className="space-y-14 md:space-y-16">
              {features.map((f, i) => (
                <FeatureBlock key={f.title} feature={f} i={i} />
              ))}
            </div>
          </ScrollConnectedPath>
        </div>
      </section>

      {/* BUILT FOR INDIAN SCHOOLS */}
      <section className="relative py-14 md:py-28 border-t border-white/10" data-testid="schoolos-india">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <SectionKicker index="[02]">Built for Indian schools</SectionKicker>
          </Reveal>
          <Reveal delay={0.1} variant="mask">
            <h2 className="mt-4 mb-12 font-heading font-bold tracking-normal text-3xl md:text-4xl text-white max-w-2xl">
              Indian first. Not localised later.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-5">
            {builtForIndia.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-white/10 bg-surface p-7 transition-colors duration-500 hover:border-cyan-accent/30">
                  <h3 className="font-heading text-xl tracking-normal text-white">{b.title}</h3>
                  <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{b.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* COMPLIANCE & TRUST */}
      <section className="relative py-14 md:py-28 border-t border-white/10 bg-surface/40" data-testid="schoolos-compliance">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <Reveal>
              <SectionKicker index="[03]">Compliance & trust</SectionKicker>
            </Reveal>
            <Reveal delay={0.1} variant="mask">
              <h2 className="mt-4 font-heading font-bold tracking-normal text-3xl md:text-4xl text-white">
                Sourced from the primary legislation.
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-5 text-zinc-400 leading-relaxed">{compliance.intro}</p>
            </Reveal>
          </div>
          <div className="md:col-span-7">
            <Reveal delay={0.15}>
              <div className="rounded-2xl border border-white/10 bg-surface p-8">
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-4">
                  Built on
                </p>
                <ul className="space-y-3">
                  {compliance.sources.map((s) => (
                    <li key={s} className="flex items-center gap-3 font-mono text-sm text-zinc-300">
                      <span className="h-px w-5 bg-cyan-accent/60 shrink-0" />
                      {s}
                    </li>
                  ))}
                </ul>
                <div className="mt-7 border-t border-white/10 pt-5 space-y-1.5">
                  <p className="text-sm text-zinc-400">{compliance.builder}</p>
                  <p className="font-mono text-xs tracking-wider text-cyan-accent">
                    {compliance.status}
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHERE IT STANDS — built vs in progress, stated plainly */}
      <section className="relative py-14 md:py-28 border-t border-white/10" data-testid="schoolos-status">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <SectionKicker index="[04]">Where it stands</SectionKicker>
          </Reveal>
          <Reveal delay={0.1} variant="mask">
            <h2 className="mt-4 mb-12 font-heading font-bold tracking-normal text-3xl md:text-4xl text-white max-w-2xl">
              What works today. What doesn't yet.
            </h2>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-5" data-testid="schoolos-built">
            {status.built.map((r, i) => (
              <Reveal key={r.title} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-cyan-accent/25 bg-surface p-7">
                  <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-cyan-accent">
                    Built and running
                  </p>
                  <h3 className="mt-3 font-heading text-xl tracking-normal text-white">{r.title}</h3>
                  <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{r.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-5 grid md:grid-cols-4 gap-5" data-testid="schoolos-in-progress">
            {status.inProgress.map((r, i) => (
              <Reveal key={r.title} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-white/10 bg-surface p-7 transition-colors duration-500 hover:border-cyan-accent/30">
                  <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-400">
                    In progress
                  </p>
                  <h3 className="mt-3 font-heading text-xl tracking-normal text-white">{r.title}</h3>
                  <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{r.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2}>
            <p className="mt-10 text-zinc-400 leading-relaxed">{status.note}</p>
          </Reveal>
        </div>
      </section>

      {/* FAQ — reuses the site's accordion pattern */}
      <div className="border-t border-white/10 pt-14 md:pt-24">
        <FAQSection
          items={schoolosFaq}
          kicker="SchoolOS, answered"
          heading="What schools ask us first."
          testid="schoolos-faq"
        />
      </div>

      {/* FINAL CTA */}
      <section className="relative py-16 md:py-36 border-t border-white/10 overflow-hidden" data-testid="schoolos-cta">
        <div className="tech-grid grid-fade absolute inset-0 opacity-30" />
        <div className="glow-orb animate-pulse-glow absolute left-1/2 top-1/2 h-[460px] w-[460px] -translate-x-1/2 -translate-y-1/2" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-400">
              In active development
            </p>
          </Reveal>
          <Reveal delay={0.1} variant="mask">
            <h2 className="mt-6 font-heading font-black uppercase text-4xl md:text-6xl text-white max-w-4xl mx-auto leading-[0.95]">
              Help shape it.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-10 flex justify-center">
              <MagneticButton
                onClick={() => ctaPanel.open(SCHOOLOS_CTA)}
                testid="schoolos-footer-cta"
              >
                Get in touch
              </MagneticButton>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
